import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { Tooltip } from '@mui/material';

import JxIconButton from '../JxButtons/JxIconButton';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import './JxTitleBar.css';
import './JxTitleBar-toolwindow.css';

/**
 * JxTitleBar — themed draggable panel titlebar.
 * Pure painter — spread dragHandleProps from useJxDraggablePanel directly onto it.
 *
 * @param {string} id - explicit DOM id, e.g. `${_name}_titlebar` from the caller
 * @param {string} title
 * @param {React.ReactNode} [icon] - optional dialog-identity icon shown left of the title (e.g. SettingsIcon for a settings dialog). No default — omit for a plain text titlebar.
 * @param {boolean} isDraggable - when true, dragHandleProps are applied AND a grip icon is shown.
 *   The grip is visual-only feedback ("this bar can be dragged") — the entire bar is the
 *   actual drag handle either way, the icon itself has no extra drag logic.
 * @param {object} dragHandleProps - from useJxDraggablePanel, only used when isDraggable
 * @param {React.ReactNode|Array<{icon: React.ReactNode, title: string, onClick: () => void}>} actions
 *   Coder choice: inline JSX (a Fragment of JxIconButton's) for a small dlg with 1-2 actions,
 *   or an array of { icon, title, onClick } when there are too many actions to read inline —
 *   JxTitleBar renders the JxIconButton's wrapped in MUI Tooltip + tooltipSxElevated for the
 *   themed tooltip (standaard Jerny-look — gradient/border, zie jernyTooltipStyling.js en
 *   ContentPanelHeader.jsx als referentie; NIET JxTooltip, dat geeft een vlakkere look —
 *   gecorrigeerd 2026-07-08, zie Development.Patterns rk=ed2d2d36-15de-4867-8b09-552a7801b626).
 *   Inline JSX consumers are responsible for their own tooltip wrapping.
 * @param {'default'|'toolwindow'} [variant] - 'toolwindow' is compact/uppercase (see JxTitleBar-toolwindow.css),
 *   for small floating panels. 'default' is the regular dialog titlebar.
 * @param {boolean} [isMinimizable] - when true, shows a minimize icon button. Pure rendering flag,
 *   same shape as isDraggable — JxTitleBar does not know what minimize means, it only fires
 *   ComponentNotifier.notify('minimize', windowId) on click. Actual minimize logic lives in
 *   JxMinimizableFormBase, which subscribes to that event (see Development.Patterns note).
 * @param {string} [windowId] - target id passed through to the notify('minimize', windowId) call.
 *   Required when isMinimizable is true.
 * @param {(e: React.MouseEvent) => void} [onDoubleClick] - optional double-click handler on the titlebar itself,
 *   e.g. to toggle a collapsible filter/toolbar row in the panel body.
 */
export default function JxTitleBar({
    id,
    title,
    icon,
    isDraggable = true,
    dragHandleProps,
    actions,
    variant = 'default',
    isMinimizable = false,
    windowId,
    onDoubleClick,
}) {
    const handleMinimizeClick = (e) => {
        e.stopPropagation();
        ComponentNotifier.notify('minimize', windowId);
    };

    const renderedActions = Array.isArray(actions)
        ? actions.map((a) => (
            <Tooltip key={a.title} title={a.title} componentsProps={tooltipSxElevated}>
                <JxIconButton icon={a.icon} onClick={a.onClick} />
            </Tooltip>
        ))
        : actions;

    return (
        <div
            id={id}
            className={`jx-titlebar${variant === 'toolwindow' ? ' jx-titlebar-toolwindow' : ''}${isDraggable ? ' jx-titlebar-draggable' : ''}`}
            onDoubleClick={onDoubleClick}
            {...(isDraggable ? dragHandleProps : {})}
        >
            <div className="jx-titlebar-left">
                {isDraggable && <DragIndicatorIcon className="jx-titlebar-grip" sx={{ fontSize: 16 }} />}
                {icon}
                <span className="jx-titlebar-label">{title}</span>
            </div>
            {(isMinimizable || renderedActions) && (
                <div className="jx-titlebar-actions">
                    {isMinimizable && (
                        <Tooltip title="Minimize" componentsProps={tooltipSxElevated}>
                            <JxIconButton icon={<MinimizeIcon sx={{ fontSize: 14 }} />} onClick={handleMinimizeClick} />
                        </Tooltip>
                    )}
                    {renderedActions}
                </div>
            )}
        </div>
    );
}
